import { useEffect, useRef, useState } from "react";
import { Video, Mic, MicOff, X, RotateCcw, UploadCloud, Square, Dot } from "lucide-react";
import { toast } from "sonner";

const MAX_SECONDS = 60;

const MIME_PREFERENCES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
];

function pickMimeType() {
  if (typeof MediaRecorder === "undefined") return null;
  for (const m of MIME_PREFERENCES) {
    if (MediaRecorder.isTypeSupported(m)) return m;
  }
  return null;
}

/** Modal that opens the camera, records up to 60 s, then hands the resulting File back via onConfirm. */
export default function WebcamRecorder({ open, onClose, onConfirm }) {
  const liveVideoRef = useRef(null);
  const playbackRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const tickRef = useRef(null);

  const [phase, setPhase] = useState("idle"); // idle | recording | review
  const [seconds, setSeconds] = useState(0);
  const [muted, setMuted] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [supported] = useState(() => typeof MediaRecorder !== "undefined" && !!navigator.mediaDevices?.getUserMedia);

  // Acquire camera when modal opens
  useEffect(() => {
    if (!open) return;
    if (!supported) {
      toast.error("Your browser doesn't support in-browser recording. Please upload a file instead.");
      onClose?.();
      return;
    }
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: "user" },
          audio: true,
        });
        streamRef.current = stream;
        if (liveVideoRef.current) {
          liveVideoRef.current.srcObject = stream;
        }
      } catch (e) {
        toast.error("Couldn't access your camera. Please allow camera permission and try again.");
        onClose?.();
      }
    })();
    return () => cleanup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const cleanup = () => {
    if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
    recorderRef.current = null;
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mime = pickMimeType();
    let rec;
    try {
      rec = mime ? new MediaRecorder(streamRef.current, { mimeType: mime }) : new MediaRecorder(streamRef.current);
    } catch (e) {
      toast.error("Recording isn't supported on this device.");
      return;
    }
    rec.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: rec.mimeType || "video/webm" });
      const url = URL.createObjectURL(blob);
      setRecordedBlob(blob);
      setBlobUrl(url);
      setPhase("review");
      if (tickRef.current) { clearInterval(tickRef.current); tickRef.current = null; }
    };
    recorderRef.current = rec;
    rec.start(250);
    setPhase("recording");
    setSeconds(0);
    tickRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) {
          stopRecording();
          return MAX_SECONDS;
        }
        return s + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try { recorderRef.current.stop(); } catch { /* ignore */ }
    }
  };

  const reRecord = () => {
    if (blobUrl) { URL.revokeObjectURL(blobUrl); }
    setBlobUrl(null);
    setRecordedBlob(null);
    setSeconds(0);
    setPhase("idle");
  };

  const toggleMute = () => {
    if (!streamRef.current) return;
    const next = !muted;
    streamRef.current.getAudioTracks().forEach((t) => (t.enabled = !next));
    setMuted(next);
  };

  const confirm = () => {
    if (!recordedBlob) return;
    const ext = (recordedBlob.type || "video/webm").includes("mp4") ? "mp4" : "webm";
    const file = new File([recordedBlob], `intro-${Date.now()}.${ext}`, { type: recordedBlob.type || "video/webm" });
    onConfirm?.(file);
    handleClose();
  };

  const handleClose = () => {
    cleanup();
    setPhase("idle");
    setSeconds(0);
    setBlobUrl(null);
    setRecordedBlob(null);
    onClose?.();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-brand-ink/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="webcam-recorder">
      <div className="bg-white rounded-lg shadow-2xl max-w-2xl w-full overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-brand-line">
          <div className="flex items-center gap-2">
            <Video className="h-4 w-4 text-brand-maroon" />
            <p className="overline">Record video introduction</p>
          </div>
          <button onClick={handleClose} className="p-1 rounded-full hover:bg-brand-muted" data-testid="webcam-close" aria-label="Close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="relative bg-black aspect-video">
          {/* Live preview when not in review */}
          <video
            ref={liveVideoRef}
            autoPlay
            playsInline
            muted
            className={`absolute inset-0 w-full h-full object-cover ${phase === "review" ? "hidden" : ""}`}
          />
          {/* Playback when reviewing */}
          {phase === "review" && (
            <video
              ref={playbackRef}
              src={blobUrl}
              controls
              autoPlay
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="webcam-playback"
            />
          )}

          {/* REC pill */}
          {phase === "recording" && (
            <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-brand-maroon text-white text-xs px-2.5 py-1 rounded-full">
              <Dot className="h-4 w-4 animate-pulse" strokeWidth={6} /> REC {seconds}s
            </div>
          )}

          {/* Time-cap bar */}
          {phase === "recording" && (
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/15">
              <div className="h-full bg-brand-gold transition-all" style={{ width: `${(seconds / MAX_SECONDS) * 100}%` }} />
            </div>
          )}

          {/* Empty-state when stream initialising */}
          {phase === "idle" && !streamRef.current && (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">Initialising camera…</div>
          )}
        </div>

        <div className="px-5 py-4 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs text-brand-inkLight max-w-xs">
            {phase === "review"
              ? "Happy with it? Save it to your profile or re-record."
              : `Up to ${MAX_SECONDS} seconds. Speak clearly, smile, and share what you're looking for.`}
          </p>

          <div className="flex items-center gap-2">
            {phase !== "review" && (
              <button onClick={toggleMute} className="p-2 rounded-full border border-brand-line hover:bg-brand-muted" title={muted ? "Unmute mic" : "Mute mic"} data-testid="webcam-mute">
                {muted ? <MicOff className="h-4 w-4 text-brand-maroon" /> : <Mic className="h-4 w-4 text-brand-ink" />}
              </button>
            )}

            {phase === "idle" && (
              <button onClick={startRecording} className="btn-primary text-sm py-2 flex items-center gap-2" data-testid="webcam-start">
                <Dot className="h-4 w-4" strokeWidth={6} /> Start recording
              </button>
            )}
            {phase === "recording" && (
              <button onClick={stopRecording} className="btn-ghost text-sm py-2 flex items-center gap-2" data-testid="webcam-stop">
                <Square className="h-3.5 w-3.5" /> Stop
              </button>
            )}
            {phase === "review" && (
              <>
                <button onClick={reRecord} className="btn-ghost text-sm py-2 flex items-center gap-2" data-testid="webcam-rerecord">
                  <RotateCcw className="h-3.5 w-3.5" /> Re-record
                </button>
                <button onClick={confirm} className="btn-primary text-sm py-2 flex items-center gap-2" data-testid="webcam-confirm">
                  <UploadCloud className="h-4 w-4" /> Save to profile
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
